"use client";

import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";
import { Check } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { MarketingHeader, MarketingFooter, MarketingCTA } from "@/components/marketing";

const pricingTiers = [
  {
    name: "Free",
    monthlyPrice: "$0",
    annualPrice: "$0",
    description: "For small teams getting started",
    features: [
      "Unlimited members",
      "Up to 2 teams",
      "Up to 5 projects",
      "5 summaries/month",
      "1 Slack channel",
      "Updates feed",
      "Workflows",
    ],
    cta: "Get started",
    ctaLink: "/auth",
    highlighted: false,
  },
  {
    name: "Pro",
    monthlyPrice: "$8",
    annualPrice: "$5",
    description: "For growing teams that need more",
    features: [
      "Everything in Free",
      "Unlimited teams",
      "Unlimited projects",
      "Unlimited summaries",
      "Unlimited Slack channels",
      "Priority support",
    ],
    cta: "Get started",
    ctaLink: "/auth",
    highlighted: true,
  },
  {
    name: "Enterprise",
    monthlyPrice: "Custom",
    annualPrice: "Custom",
    description: "For large organizations",
    features: [
      "Everything in Pro",
      "SSO & SAML",
      "Advanced security controls",
      "Custom workflows",
      "Dedicated support",
      "SLA guarantees",
      "Onboarding assistance",
    ],
    cta: "Contact sales",
    ctaLink: "/support",
    highlighted: false,
  },
];

export default function PricingPage() {
  const [isAnnual, setIsAnnual] = useState(true);
  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <MarketingHeader variant="light" />

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-8 text-center">
        <h1 className="text-5xl md:text-6xl font-bold mb-6">Pricing</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
          Use {BRAND.name} for free with your whole team. Upgrade to unlock
          advanced workflows, unlimited teams, and enhanced collaboration
          features.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <span
            className={`text-lg transition-colors ${!isAnnual ? "text-gray-900 font-medium" : "text-gray-400"}`}
          >
            Monthly
          </span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className="relative w-16 h-8 bg-gray-200 rounded-full transition-colors hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
            aria-label={`Switch to ${isAnnual ? "monthly" : "annual"} billing`}
          >
            <span
              className={`absolute top-1 left-1 w-6 h-6 bg-gray-900 rounded-full transition-transform duration-200 ease-in-out ${
                isAnnual ? "translate-x-8" : "translate-x-0"
              }`}
            />
          </button>
          <span
            className={`text-lg transition-colors ${isAnnual ? "text-gray-900 font-medium" : "text-gray-400"}`}
          >
            Annual
          </span>
          {isAnnual && (
            <span className="ml-2 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
              Save 37%
            </span>
          )}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {pricingTiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative rounded-xl border p-8 flex flex-col ${
                tier.highlighted
                  ? "border-gray-900 bg-gray-50 shadow-lg"
                  : "border-gray-200 bg-white"
              }`}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most popular
                </div>
              )}

              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                <div className="mb-3">
                  <span className="text-4xl font-bold">
                    {isAnnual ? tier.annualPrice : tier.monthlyPrice}
                  </span>
                  {tier.name === "Pro" && (
                    <span className="text-gray-500 ml-2">per member/month</span>
                  )}
                </div>
                <p className="text-gray-600">{tier.description}</p>
              </div>

              <div className="flex-1 mb-8">
                <ul className="space-y-4">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                asChild
                className={`w-full ${
                  tier.highlighted
                    ? "bg-gray-900 text-white hover:bg-gray-800"
                    : "bg-white border border-gray-300 text-gray-900 hover:bg-gray-50"
                }`}
              >
                <Link href={tier.ctaLink}>{tier.cta}</Link>
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 py-20 border-t border-gray-200">
        <h2 className="text-3xl font-bold mb-12 text-center">
          Frequently asked questions
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Can I try {BRAND.name} before paying?
            </h3>
            <p className="text-gray-600">
              Yes! The Free plan includes unlimited members, so your whole team
              can use {BRAND.name} at no cost. Upgrade to Pro when you need
              unlimited teams, projects, summaries, and Slack channels.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              What counts as a member?
            </h3>
            <p className="text-gray-600">
              A member is anyone who can post updates, comment, or create
              content in your organization. Viewers are always free and can see
              content but cannot post or comment. On the Free plan, you can have
              unlimited members and viewers.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              Can I change plans later?
            </h3>
            <p className="text-gray-600">
              Absolutely. You can upgrade or downgrade at any time. When you
              upgrade, you'll be charged immediately. When you downgrade, the
              change takes effect at the end of your current billing period.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              What payment methods do you accept?
            </h3>
            <p className="text-gray-600">
              We accept all major credit cards through Stripe. Enterprise
              customers can also pay via invoice with NET 30 terms.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">
              What about monthly billing?
            </h3>
            <p className="text-gray-600">
              Pro is available at $8/member/month with monthly billing, or save
              37.5% by paying annually at $5/member/month ($60/member/year).
            </p>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <MarketingCTA />

      {/* Footer */}
      <MarketingFooter variant="light" />
    </div>
  );
}
